import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MobileNavDrawer } from './MobileNavDrawer';
import { TopBar } from './TopBar';
import { AIAssistantUnified } from '../assistant';
import { cn } from '../../lib/utils';
import { appEnv } from '../../lib/env';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { pathname } = useLocation();
  const hideAssistant = appEnv.isProd && pathname === '/';

  // Close the mobile drawer on any navigation
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Background gradient effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#09193b]/5 dark:bg-[#b9ff4d]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#b9ff4d]/5 dark:bg-[#b9ff4d]/3 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-72 h-72 bg-brand-navy/3 rounded-full blur-3xl" />
      </div>

      {/* Left Sidebar (desktop) */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((prev) => !prev)}
      />

      {/* Mobile navigation drawer */}
      <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      {/* Top bar */}
      <TopBar
        sidebarCollapsed={sidebarCollapsed}
        onMenuToggle={() => setMobileNavOpen(true)}
      />

      {/* Main content */}
      <main
        className={cn(
          'relative pt-14 min-h-screen transition-all duration-300',
          sidebarCollapsed ? 'ml-0 md:ml-16' : 'ml-0 md:ml-56'
        )}
      >
        <div className="p-4 md:p-6">{children}</div>
      </main>

      {/* Global AI Assistant (handles both dashboard card and sidebar modes) */}
      {!hideAssistant && <AIAssistantUnified />}
    </div>
  );
}

